/**
 * Rollforward Screen
 *
 * Shows a daily receivables reconciliation: opening balance, activity
 * (Sales, Collection, Dilution, Reversals), ending balance, and any
 * discrepancy between the computed and actual ending balance.
 *
 * Data flow:
 *  - Reads `data.rollforward` and `project.hasRollforward` from Redux.
 *  - Fetches cached rollforward data on mount if previously calculated.
 *  - "Calculate" dispatches `calculateRollforward` to the Python backend,
 *    which categorizes each invoice movement using the normMap document-type
 *    assignments configured on the DocumentTypes screen.
 *  - "Discrepancy" column highlights data quality issues where the computed
 *    ending balance does not match the next period's beginning balance.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  calculateRollforward,
  getRollforwardData,
} from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";

// -------------------------------------------------------------
// -------------------------------------------------------------
const headers = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Total Beginning", key: "Total Beginning", datatype: "float" },
  { text: "Sales", key: "Sales", datatype: "float" },
  { text: "Collection", key: "Collection", datatype: "float" },
  { text: "Dilution", key: "Dilution", datatype: "float" },
  {
    text: "Reversal(Collection)",
    key: "DMR Collection",
    datatype: "float",
  },
  { text: "Reversal(Dilution)", key: "DMR Dilution", datatype: "float" },
  { text: "Total Ending", key: "Total Ending", datatype: "float" },
  { text: "Ending Check", key: "Ending Check", datatype: "float" },
  { text: "Discrepancy", key: "Discrepancy", datatype: "float" },
];

const Rollforward = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);

  const { data: rollforward, has: hasRollforward } = useAnalysis("rollforward", getRollforwardData);
  const hasMaster = useScenarioFlag("hasMaster");

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateRollforward(() => setCalculating(false)));
          }}
        >
          {hasRollforward ? "Recalculate" : "Calculate"} Rollforward
        </button>
      </div>
      {hasRollforward && !rollforward && <SkeletonTable />}
      {rollforward && (
        <DataTable
          data={rollforward.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default Rollforward;
